// src\themes\lino\components\PasswordStepLoginForm.tsx
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';

import { useRouter } from 'next/navigation';

import PasswordInput from '@/components/account/input/PasswordInput';
import { useAuth } from '@/contexts/authContext';
import { useCartStore } from '@/core/hooks/cart/useCartStore';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { loginWithPassword } from '@/core/lib/api/main/auth';
import {
    getPasswordLoginSchema,
    PasswordLoginData,
} from '@/core/schemas/authSchema';
import { PasswordLoginStepProps } from '@/core/types/auth';

interface ExtendedPasswordLoginStepProps extends PasswordLoginStepProps {
    setStep: (step: 1 | 2 | 3) => void;
    isLoading?: boolean;
}

export default function PasswordStepLoginForm({
    handleResend,
    mobile,
    session_token,
    setStep,
    isLoading,
}: ExtendedPasswordLoginStepProps) {
    const router = useRouter();
    const { setAuth } = useAuth();
    const { dictionary } = useDictionary();
    const passwordSchema = getPasswordLoginSchema(dictionary);

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting },
    } = useForm<PasswordLoginData>({
        resolver: zodResolver(passwordSchema),
    });

    const handlePasswordLogin = async (data: PasswordLoginData) => {
        try {
            const result = await loginWithPassword({
                mobile,
                password: data.password,
                session_token,
            });

            if (result?.status === 'success') {
                setAuth(result.data);
                toast.success(result.message);
                router.push('/dashboard');

                // سینک کردن سبد خرید دیگه لازم نیست جلوی رفتن به داشبورد رو بگیره
                useCartStore.getState().syncCart(true)
                    .then(() => useCartStore.getState().fetchCartFromBackend())
                    .catch((err) => console.error('Cart sync error:', err));
            } else {
                setError('password', { type: 'server', message: result.message });
            }
        } catch (error) {
            console.error('Password login error:', error);
            toast.error(dictionary.common.error);
        }
    };

    const handleOtpLogin = async () => {
        try {
            await handleResend();
            setStep(3);
        } catch (error) {
            console.error('Failed to resend OTP code:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit(handlePasswordLogin)} className="w-full">
            <div className="flex flex-col w-full mb-8 min-h-[68px]">
                <PasswordInput
                    placeholder={dictionary.forms?.password}
                    errors={errors}
                    name="password"
                    register={register}
                    disabled={isSubmitting || isLoading}
                    label=""
                    inputClassName="w-full h-12 rounded-2xl border border-primary-300 bg-primary-0 px-6 text-center placeholder:text-primary-500 transition-colors focus:border-primary-900 focus:ring-4 focus:ring-primary-900/5 focus:outline-none"
                    variant="default"
                />
            </div>

            <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="group w-full h-12 rounded-2xl bg-primary-900 text-primary-0 text-[13px] uppercase tracking-[0.14em] hover:bg-primary-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
            >
                {isSubmitting ? (
                    <Loader2 className="animate-spin size-4" />
                ) : (
                    <>
                        {dictionary.forms?.login}
                        <ArrowRight className="size-3.5 rtl:rotate-180 transition-transform group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5" />
                    </>
                )}
            </button>

            <button
                type="button"
                onClick={handleOtpLogin}
                className="cursor-pointer mt-4 w-full text-center text-[11px] uppercase tracking-[0.14em] text-primary-500 hover:text-primary-900 transition-colors"
            >
                {dictionary.forms?.otpLogin}
            </button>
        </form>
    );
}